/*
 * Route Adapters (Normalizers)
 *
 * Purpose:
 * - Convert different upstream route payloads into one normalized FE state shape.
 * - Keep data-source-specific parsing out of UI components.
 *
 * Normalizers:
 * - normalizeCoreWaypoints:    core GET /waypoints response → normalized state
 * - normalizeAstarResponse:    live astar pathfinder GeoJSON FeatureCollection → normalized state
 * - normalizeSketchRoute:      uncomputed manual planner route (geometry only) → normalized state
 * - normalizeTreeMetadata:     tree-loaded vessel/voyage/route metadata → normalized state
 *
 * After normalization, selectRenderableRoutes (RouteSelectors.jsx) produces
 * the {points, lines} shape consumed by RoutePoints / RouteLines.
 */

import {
  createEmptyNormalizedState,
  setVesselIds,
  upsertRoutes,
  upsertVessels,
  upsertVoyages,
  upsertWaypointsByRoute,
} from "@/features/route/state/RouteState.jsx";
import { optimisedForFromRequest } from "@/utils/routeHelper";
import { KNOTS_TO_MSEC } from "@/config/defaults";

function toId(value) {
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
}

function normalizeRouteState(value) {
  if (value == null) return null;
  const state = String(value).toLowerCase();
  return state === "active" || state === "suggested" || state === "archived"
    ? state
    : null;
}

// ---------------------------------------------------------------------------
// Common waypoint shape (shared by all normalizers)
// ---------------------------------------------------------------------------

/**
 * Map any waypoint-like object into the common normalized shape.
 * Accepts both camelCase (core API) and snake_case (astar response) keys.
 */
function normalizeWaypointType(raw) {
  // Astar uses "Regular"/"Segmentize"; DB/FE uses "original_point"/"weather_point".
  // Normalize to the DB/FE convention that renderers expect.
  if (raw === "Regular") return "original_point";
  if (raw === "Segmentize") return "weather_point";
  // The DB column defaults to '' (imported/manually created waypoints); treat
  // it like null so selectors fall back to "original_point".
  return raw || null;
}

// Astar emits per-leg fuel metrics as flat keys; group them so they survive
// the save roundtrip in the waypoint's leg_data JSONB column.
const LEG_FUEL_KEYS = [
  "leg_consumption_tons",
  "leg_fuel_cost_usd",
  "leg_eca_consumption_tons",
  "leg_non_eca_consumption_tons",
  "leg_eca_fuel_cost_usd",
  "leg_non_eca_fuel_cost_usd",
  "sum_consumption_tons",
  "sum_fuel_cost_usd",
];

export function collectLegFuelData(wp) {
  let result = null;
  for (const key of LEG_FUEL_KEYS) {
    if (wp?.[key] == null) continue;
    if (result === null) result = {};
    result[key] = wp[key];
  }
  return result;
}

function normalizeWaypoint(wp, routeId) {
  // Astar sends the RNW leg_data blob and the flat fuel keys side by side on
  // the same waypoint — merge them instead of letting one shadow the other.
  const legBlob = wp?.legData ?? wp?.leg_data ?? null;
  const legFuel = collectLegFuelData(wp);
  return {
    // toId(null) would coerce to 0 because Number(null) === 0; only return an
    // id when the source actually had one, otherwise leave it null so
    // upsertWaypointsByRoute keys by number/extended/timestamp instead of
    // collapsing every "id:0" waypoint into a single Map entry.
    id: wp?.id == null ? null : toId(wp.id),
    routeId,
    number: Number.isFinite(Number(wp?.number ?? wp?.waypoint_number))
      ? Number(wp?.number ?? wp?.waypoint_number)
      : 0,
    extendedNumber: Number.isFinite(
      Number(
        wp?.extendedNumber ?? wp?.extended_number ?? wp?.waypoint_subnumber,
      ),
    )
      ? Number(
          wp?.extendedNumber ?? wp?.extended_number ?? wp?.waypoint_subnumber,
        )
      : 0,
    legtype:
      (wp?.legtype ?? wp?.leg_type ?? wp?.segment_type)?.toLowerCase() ?? null,
    basespeed: wp?.basespeed ?? wp?.plan_basespeed_kmh ?? null,
    timestamp: wp?.timestamp ?? wp?.datetime_utc ?? null,
    type: normalizeWaypointType(wp?.type ?? wp?.waypoint_type),
    lonlat:
      Array.isArray(wp?.lonlat) && wp.lonlat.length === 2
        ? [Number(wp.lonlat[0]), Number(wp.lonlat[1])]
        : null,
    // Speed
    sog: wp?.sog ?? wp?.sog_knots ?? null,
    heading: wp?.heading ?? wp?.bearing_to_next_waypoint_deg_north ?? null,
    baseSpeedAdjustedKmh:
      wp?.baseSpeedAdjustedKmh ?? wp?.base_speed_adjusted_kmh ?? null,
    // Distance / time
    legDistanceKm: wp?.legDistanceKm ?? wp?.leg_distance_km ?? null,
    legTimeHour: wp?.legTimeHour ?? wp?.leg_time_hour ?? null,
    legSegmentDistanceKm:
      wp?.legSegmentDistanceKm ?? wp?.leg_segment_distance_km ?? null,
    legSegmentTimeHour:
      wp?.legSegmentTimeHour ?? wp?.leg_segment_time_hour ?? null,
    sumDistanceKm: wp?.sumDistanceKm ?? wp?.sum_distance_km ?? null,
    sumTimeHour: wp?.sumTimeHour ?? wp?.sum_time_hour ?? null,
    // Pathfinder snapshot blobs (JSONB from DB or flat from astar — stored as-is)
    weatherSnapshot: wp?.weatherSnapshot ?? wp?.weather_snapshot ?? null,
    legData: legFuel ? { ...(legBlob ?? {}), ...legFuel } : legBlob,
    rnwEvaluation: wp?.rnwEvaluation ?? wp?.rnw_evaluation ?? null,
    restrictions: wp?.restrictions ?? null,
    warnings: wp?.warnings ?? null,
    // Reported route data
    albisReports: wp?.albisReports ?? wp?.albis_reports ?? null,
    albisSensors: wp?.albisSensors ?? wp?.albis_sensors ?? null,
  };
}

// ---------------------------------------------------------------------------
// normalizeCoreWaypoints — core GET /waypoints response
// ---------------------------------------------------------------------------

export function normalizeCoreWaypoints(waypoints = []) {
  let next = createEmptyNormalizedState();

  const normalizedWaypoints = [];
  const routesById = {};
  const voyagesById = {};
  const vesselsById = {};

  (waypoints ?? []).forEach((wp) => {
    const routeId = toId(wp?.routeId ?? wp?.route_id);
    if (routeId == null) return;
    const voyageId = toId(wp?.voyageId ?? wp?.voyage_id);
    const vesselId = toId(wp?.vesselId ?? wp?.vessel_id);

    normalizedWaypoints.push(normalizeWaypoint(wp, routeId));

    const routeType =
      wp?.routeType ??
      wp?.route_type ??
      routesById[routeId]?.routeType ??
      "planned";
    const routePatch = {
      ...(routesById[routeId] ?? {}),
      id: routeId,
      routeType,
    };
    const state =
      normalizeRouteState(wp?.routeState ?? wp?.route_state) ??
      routesById[routeId]?.state ??
      (routeType === "planned" ? "active" : null);
    if (state != null) routePatch.state = state;
    if (voyageId != null) routePatch.voyageId = voyageId;
    const wpVesselId = toId(wp?.vesselId ?? wp?.vessel_id);
    if (wpVesselId != null) routePatch.vesselId = wpVesselId;
    if (wp?.vesselName != null || wp?.vessel_name != null) {
      routePatch.vesselName = wp?.vesselName ?? wp?.vessel_name;
    }
    if (wp?.routeName != null || wp?.route_name != null) {
      routePatch.name = wp?.routeName ?? wp?.route_name;
    }
    routesById[routeId] = routePatch;

    if (voyageId != null) {
      const voyagePatch = {
        ...(voyagesById[voyageId] ?? {}),
        id: voyageId,
      };
      if (wpVesselId != null) voyagePatch.vesselId = wpVesselId;
      if (wp?.voyageName != null || wp?.voyage_name != null) {
        voyagePatch.name = wp?.voyageName ?? wp?.voyage_name;
      }
      voyagesById[voyageId] = voyagePatch;
    }

    if (vesselId != null) {
      const vesselPatch = {
        ...(vesselsById[vesselId] ?? {}),
        id: vesselId,
      };
      if (wp?.vesselName != null || wp?.vessel_name != null) {
        vesselPatch.name = wp?.vesselName ?? wp?.vessel_name;
      }
      vesselsById[vesselId] = vesselPatch;
    }
  });

  next = upsertVessels(next, Object.values(vesselsById));
  next = upsertVoyages(next, Object.values(voyagesById));
  next = upsertRoutes(next, Object.values(routesById));
  next = upsertWaypointsByRoute(next, normalizedWaypoints);

  return next;
}

// ---------------------------------------------------------------------------
// normalizeAstarResponse — live pathfinder GeoJSON FeatureCollection
// ---------------------------------------------------------------------------

const SYNTHETIC_ROUTE_ID = -1;

export function normalizeAstarResponse(
  fc,
  {
    routeId = SYNTHETIC_ROUTE_ID,
    routeType = "Pathfinder",
    vesselName = "Pathfinder",
    // Origin flag; omit to inherit (an on-map recalc of an import draft must
    // not reflag it as a planner draft).
    source = null,
    // Frozen at compute time; the draft keeps its original vessel/voyage.
    draftContext = null,
    // Raw astar FC for the per-draft export; omit (not null) to inherit.
    rawResponse = null,
  } = {},
) {
  let next = createEmptyNormalizedState();

  if (!fc || fc.type !== "FeatureCollection") return next;

  const features = fc.features ?? [];

  // Extract waypoint Point features (same filter as _getAstarPoints)
  const pointFeatures = features
    .filter(
      (f) =>
        f?.geometry?.type === "Point" && f?.properties?.waypoint_number != null,
    )
    .sort((a, b) => {
      const wna = a.properties.waypoint_number;
      const wnb = b.properties.waypoint_number;
      if (wna !== wnb) return wna - wnb;
      return (
        (a.properties.waypoint_subnumber ?? 0) -
        (b.properties.waypoint_subnumber ?? 0)
      );
    });

  // Build waypoints using the common normalizer.
  // For astar features, merge geometry coordinates into a lonlat-like shape
  // so normalizeWaypoint can pick them up.
  const normalizedWaypoints = pointFeatures.map((f) => {
    const props = f.properties ?? {};
    const [lon, lat] = f.geometry?.coordinates ?? [0, 0];
    return normalizeWaypoint(
      {
        ...props,
        lonlat: [lon, lat],
        // Map astar weather fields into a weatherSnapshot blob so the
        // normalized shape is consistent with DB-persisted waypoints.
        weather_snapshot: buildWeatherSnapshot(props),
        // Map astar ww_* fields into an rnwEvaluation blob.
        rnw_evaluation: buildRnwEvaluation(props),
        // Map astar warning_* fields into a warnings blob.
        warnings: buildWarnings(props),
        // Map astar ww_parse_tree/restriction_text into restrictions blob.
        restrictions: buildRestrictions(props),
      },
      routeId,
    );
  });

  // Extract json_request feature for route metadata
  const requestFeature = features.find(
    (f) => f?.properties?.type === "json_request",
  );
  const jsonRequest = requestFeature?.properties
    ? Object.fromEntries(
        Object.entries(requestFeature.properties).filter(
          ([k]) => k !== "type" && k !== "paths",
        ),
      )
    : null;

  // Extract route_summary for route metadata
  const summaryFeature = features.find(
    (f) => f?.properties?.type === "route_summary",
  );
  const summary = summaryFeature?.properties ?? null;

  // Optimization target from core's echoed request — authoritative (core owns
  // the fuel→time downgrade via the weights), unlike the FE's request flags.
  const optimisedFor = optimisedForFromRequest(jsonRequest);

  // Context only when provided — ROUTE_REPLACED merges entities, so a
  // recalc without draftContext keeps the originally frozen context.
  const routeEntity = {
    id: routeId,
    routeType,
    vesselName,
    // No name: the merge inherits a user-given draft name across recalcs;
    // discard paths null it via createDraftDiscardState.
    jsonRequest,
    summary,
    // Written unconditionally like jsonRequest/summary: the entity always
    // reflects this compute; null clears a stale label on merge.
    optimisedFor,
    sketch: false,
  };
  if (toId(routeId) < 0) {
    routeEntity.timestamp = new Date().toISOString();
  }
  if (source) {
    routeEntity.source = source;
  }
  if (draftContext) {
    routeEntity.vesselId = toId(draftContext.vesselId);
    routeEntity.voyageId = toId(draftContext.voyageId);
    routeEntity.draftContext = draftContext;
  }
  if (rawResponse) {
    routeEntity.rawResponse = rawResponse;
  }
  next = upsertRoutes(next, [routeEntity]);

  next = upsertWaypointsByRoute(next, normalizedWaypoints);

  return next;
}

// ---------------------------------------------------------------------------
// normalizeImportedRoute — parsed METEO PILOT import file → draft fragment
// ---------------------------------------------------------------------------

// Legacy voyage file convention: legType 0 = greatcircle, 1 = rhumbline
// (numbers would crash normalizeWaypoint's toLowerCase).
function importLegtype(legType) {
  const asString = String(legType ?? "").toLowerCase();
  return Number(legType) === 0 || asString === "greatcircle"
    ? "greatcircle"
    : "rhumbline";
}

// The entity deliberately carries NO vesselId/voyageId: the planner's
// one-draft-per-vessel policy matches by vesselId and must never see
// import drafts.
export function normalizeImportedRoute(importDraft, { routeId }) {
  let next = createEmptyNormalizedState();
  const targetId = toId(routeId);
  if (targetId == null || !importDraft) return next;

  const wps = importDraft?.sourceData?.baseRoute?.wps ?? [];
  // Voyage-file baseSpeed is knots (same convention as core's import).
  const baseSpeedKn = Number(importDraft?.sourceData?.baseSpeed);
  const baseSpeed = Number.isFinite(baseSpeedKn)
    ? baseSpeedKn * KNOTS_TO_MSEC
    : NaN;

  const normalizedWaypoints = wps
    .map((wp, idx) => {
      const lon = Number(wp?.lon);
      const lat = Number(wp?.lat);
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
      const ts = wp?.calculatedTimestamp;
      const parsedMs = ts == null ? NaN : new Date(ts).getTime();
      return normalizeWaypoint(
        {
          number: idx + 1,
          lonlat: [lon, lat],
          legtype: importLegtype(wp?.legType),
          basespeed: Number.isFinite(baseSpeed) ? baseSpeed : null,
          timestamp: Number.isNaN(parsedMs)
            ? null
            : new Date(parsedMs).toISOString(),
          type: "original_point",
        },
        targetId,
      );
    })
    .filter(Boolean);

  next = upsertRoutes(next, [
    {
      id: targetId,
      routeType: "Pathfinder",
      source: "import",
      name: importDraft?.routeName ?? null,
      vesselName: importDraft?.vesselName
        ? `${importDraft.vesselName} · Import`
        : "Import",
    },
  ]);
  next = upsertWaypointsByRoute(next, normalizedWaypoints);

  return next;
}

// ---------------------------------------------------------------------------
// normalizeSketchRoute — uncomputed manual route (planner Manual mode)
// ---------------------------------------------------------------------------

// Analysis fields nulled explicitly: ROUTE_REPLACED merges, a sketch must not inherit a stale result.
export function normalizeSketchRoute(
  routeId,
  features = [],
  { vesselName = null, draftContext = null } = {},
) {
  let next = createEmptyNormalizedState();
  const targetId = toId(routeId);
  if (targetId == null) return next;

  const normalizedWaypoints = (features ?? []).map((f, idx) => {
    const props = f?.properties ?? {};
    const [lon, lat] = f?.geometry?.coordinates ?? [0, 0];
    return normalizeWaypoint(
      {
        number: idx + 1,
        lonlat: [lon, lat],
        legtype: props.legtype ?? props.legType ?? "greatcircle",
        type: "original_point",
      },
      targetId,
    );
  });

  const routeEntity = {
    id: targetId,
    routeType: "Pathfinder",
    source: "planner",
    sketch: true,
    optimisedFor: "manual",
    jsonRequest: null,
    summary: null,
    rawResponse: null,
    timestamp: new Date().toISOString(),
  };
  if (vesselName) routeEntity.vesselName = vesselName;
  if (draftContext) {
    // Number(null) is 0: a vessel-less sketch must stay unlinked to be found again.
    if (draftContext.vesselId != null) routeEntity.vesselId = toId(draftContext.vesselId);
    if (draftContext.voyageId != null) routeEntity.voyageId = toId(draftContext.voyageId);
    routeEntity.draftContext = draftContext;
  }
  next = upsertRoutes(next, [routeEntity]);
  next = upsertWaypointsByRoute(next, normalizedWaypoints);

  return next;
}

// ---------------------------------------------------------------------------
// Astar response → JSONB blob builders
// ---------------------------------------------------------------------------
// These group the flat astar waypoint properties into the same JSONB shapes
// that the DB stores, so the normalized waypoint format is identical
// regardless of source.

function buildWeatherSnapshot(props) {
  const ws = {};
  for (const [key, value] of Object.entries(props)) {
    if (
      value !== undefined &&
      (key.startsWith("wind_") ||
        key.startsWith("wave_") ||
        key.startsWith("current_"))
    ) {
      ws[key] = value;
    }
  }
  return Object.keys(ws).length > 0 ? ws : null;
}

function buildRnwEvaluation(props) {
  const keys = [
    "ww_nav_cat",
    "ww_is_free_vessel_vertical_clearance",
    "ww_is_free_usage",
    "ww_is_closed_viaarea",
    "ww_is_leg_usable",
    "ww_maxspeed_knots",
    "ww_maxspeed_kmh",
    "ww_minspeed_knots",
    "ww_minspeed_kmh",
  ];
  const result = {};
  keys.forEach((k) => {
    if (props[k] !== undefined) result[k] = props[k];
  });
  return Object.keys(result).length > 0 ? result : null;
}

function buildWarnings(props) {
  const result = {};
  for (const [key, value] of Object.entries(props)) {
    if (key.startsWith("warning_") && value !== undefined) {
      result[key] = value;
    }
  }
  return Object.keys(result).length > 0 ? result : null;
}

function buildRestrictions(props) {
  const keys = [
    "ww_parse_tree",
    "ww_parse_tree_result",
    "ww_parse_tree_substitution",
    "ww_restriction_text_full",
    "ww_restriction_text_single",
    "ww_restriction_text_json",
    "ww_restriction_text_check",
  ];
  const result = {};
  keys.forEach((k) => {
    if (props[k] !== undefined) result[k] = props[k];
  });
  return Object.keys(result).length > 0 ? result : null;
}

// ---------------------------------------------------------------------------
// normalizeRenderedRoute — { points: { geojson }, lines: { geojson } } → fragment
// ---------------------------------------------------------------------------
//
// The drag/insert/delete handlers operate on the rendered shape that
// selectRenderableRoute produces. To dispatch a store update from inside
// those handlers, we need to convert that shape back to a normalized
// fragment. This helper does that — it walks the points geojson and feeds
// each feature through normalizeWaypoint so the result drops into the same
// canonical pipeline an astar/DB response uses.

export function normalizeRenderedRoute(routeId, renderedRoute) {
  let next = createEmptyNormalizedState();
  const targetId = toId(routeId);
  if (targetId == null || !renderedRoute) return next;

  const features = renderedRoute?.points?.geojson?.features ?? [];

  const normalizedWaypoints = features.map((f, idx) => {
    const props = f?.properties ?? {};
    const [lon, lat] = f?.geometry?.coordinates ?? [0, 0];
    // The rendered feature carries the same keys normalizeWaypoint expects
    // (sumDistanceKm, weather_snapshot, leg_data, …) plus a `lonlat` derived
    // from the geometry. `number` defaults to the array index when absent.
    return normalizeWaypoint(
      {
        ...props,
        lonlat: [lon, lat],
        number: props.number ?? idx + 1,
      },
      targetId,
    );
  });

  next = upsertRoutes(next, [
    {
      id: targetId,
      routeType: renderedRoute?.points?.routeType ?? "Pathfinder",
      vesselName: renderedRoute?.points?.vesselName ?? null,
      name: renderedRoute?.points?.name ?? null,
      jsonRequest: renderedRoute?.jsonRequest ?? null,
      summary: renderedRoute?.summary ?? null,
      optimisedFor: renderedRoute?.optimisedFor ?? null,
      // An edit through a draft's sketch view turns the draft into that sketch.
      ...(renderedRoute?.sketch ? { sketch: true, rawResponse: null } : {}),
    },
  ]);
  next = upsertWaypointsByRoute(next, normalizedWaypoints);

  return next;
}

// ---------------------------------------------------------------------------
// normalizeTreeMetadata — tree-loaded vessel/voyage/route metadata
// ---------------------------------------------------------------------------

// Optional top-level `vesselId`/`voyageId` stamp the parent link onto every
// voyage/route (API rows don't carry it reliably); a row's own value wins.
export function normalizeTreeMetadata(treeMetadata = {}) {
  let next = createEmptyNormalizedState();

  const vessels = (treeMetadata?.vessels ?? []).map((vessel) => ({
    ...vessel,
    id: toId(vessel?.id),
  }));

  const voyages = (treeMetadata?.voyages ?? []).map((voyage) => ({
    ...voyage,
    id: toId(voyage?.id),
    vesselId: toId(voyage?.vesselId ?? treeMetadata?.vesselId),
  }));

  const routes = (treeMetadata?.routes ?? []).map((route) => {
    const routeType = route?.routeType ?? route?.type ?? "planned";
    return {
      ...route,
      id: toId(route?.id),
      voyageId: toId(route?.voyageId ?? treeMetadata?.voyageId),
      routeType,
      // Legacy rows carry state=null plus an `active` flag — the flag must
      // win, else an archived row would be stamped active.
      state:
        normalizeRouteState(route?.state) ??
        (routeType === "planned"
          ? route?.active === false
            ? "archived"
            : "active"
          : null),
    };
  });

  next = upsertVessels(next, vessels);
  // Vessels named here are tree rows (GET /vessels, import seed) — put them
  // on the canonical root list. Waypoint/fleet fragments never pass through
  // this adapter, so ghost vessels stay off the list.
  next = setVesselIds(next, [
    ...(next.relations.vesselIds ?? []),
    ...vessels.map((vessel) => vessel.id),
  ]);
  next = upsertVoyages(next, voyages);
  next = upsertRoutes(next, routes);

  return next;
}
